import { TAcquisitionProviderFacade } from "../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";
import { useFeatureFlags } from "../../providers/FeatureFlagProvider/FeatureFlagProvider";
import { usePushdownContext } from "../../providers/Pushdown/PushdownProvider";
import { hasDefaultComponent } from "../../utils/pageModelUtils";
import sortPushdownErrors from "./sortPushdownErrors";
import { IFieldName } from "../../providers/Pushdown/util/getPushdownError";
import { personalInformationFieldValidations } from "./FieldValidations/CtaFieldValidations-PlasticCardValidation";
import { phoneFieldValidations } from "./FieldValidations/CtaFieldValidations-PhoneValidation";
import { incomeFieldValidations } from "./FieldValidations/CtaFieldValidations-IncomeValidation";
import { ssnFieldValidation } from "./FieldValidations/CtaFieldValidations-PersonalInformationValidation";
import { applicationEsignFieldValidations, ConsumerAcknowledgementFieldValidations, termsAndConditionsFieldValidations } from "./FieldValidations/CtaFieldValidations-RequiredAgreements";
import { authorizedUserFieldValidations, creditProtectionFieldValidations } from "./FieldValidations/CtaFieldValidations-AncillaryProducts";
import { TFieldState } from "../../providers/ModernAcquisitionProvider/types";
import { TFieldValidationFunction, TFieldValidationResult, ValidationAbortError } from "./FieldValidations";

const useHandleErrorPushdown = (): {
    handleErrorPushdown: (validatingState: TAcquisitionProviderFacade) => Promise<void>
} => {

    const { showPushdown } = usePushdownContext();
    const { getFeatureFlags } = useFeatureFlags();

    const handleErrorPushdown = async (
        validatingState: TAcquisitionProviderFacade,
    ): Promise<void> => {
        let fieldErrors: TFieldValidationResult = {};
        const acquisitionData = validatingState.getPersonalInformation();

        const ComponentNameMap: Record<
            string, 
            TFieldValidationFunction[]
        > = {
            'application-esign': [ applicationEsignFieldValidations ],
            'authorized-user': [ authorizedUserFieldValidations ],
            'credit-protection': [ creditProtectionFieldValidations ],
            'consumer-report': [ ConsumerAcknowledgementFieldValidations ],
            'personal-information-form': [ personalInformationFieldValidations ],
            'phone-number-and-income': [
                phoneFieldValidations,
                incomeFieldValidations,
            ],
            'plastic-card-design': [ personalInformationFieldValidations ],
            'ssn-module': [ ssnFieldValidation ],
            'terms-and-conditions': [ termsAndConditionsFieldValidations ],
        };
        try {
            for (const componentName in ComponentNameMap) {
                if (hasDefaultComponent(`cob-corp-acquisition/components/creditonebank/${componentName}`)) {
                    for (const validationFunction of ComponentNameMap[componentName]) {
                        const validationResult = validationFunction(validatingState);
                        if (validationResult instanceof Promise) {
                            fieldErrors = {
                                ...fieldErrors,
                                ...(await validationResult),
                            };
                        } else {
                            fieldErrors = {
                                ...fieldErrors,
                                ...validationResult,
                            }
                        }
                    }
                }
            }
        } catch (e: unknown) {
            if (e instanceof ValidationAbortError) {
                if (e.pushdownToShow) {
                    showPushdown(e.pushdownToShow);
                }

                return;
            }
        }

// NEW PROVIDER REPLACEMENT
        // If there is an existing error for the key, remove it, and assert the Field Error.
        // Refer to modernReadManager.tsx for similar implementation
        const validationMigrationMap: Partial<Record<IFieldName, keyof typeof acquisitionData>> = {};

        if (getFeatureFlags('DPM-13102')) {
            validationMigrationMap.FirstName = 'firstName';
            validationMigrationMap.LastName = 'lastName';
            validationMigrationMap.NameSuffix = 'suffix';
            // --- Always Disabled / Optional ---
            // validationMigrationMap.MiddleInitial = 'middleInitial';
        }

        if (getFeatureFlags('DPM-13566')) {
            validationMigrationMap.EmailAddress = 'emailAddress';
        }

        const overriddenFields = [
            ...Object
                .entries(fieldErrors)
                .filter(([fieldName]) => !(fieldName in validationMigrationMap)),
            ...Object
                .entries(fieldErrors)
                .filter(([fieldName]) => (fieldName in validationMigrationMap))
                .filter(([fieldName]) => (acquisitionData[fieldName as keyof typeof acquisitionData].state === "error"))
                .map(([fieldName]) => ([fieldName, 'RequiredField'])) // TODO: Doesn't handle other ErrorTypes
        ] as unknown as [IFieldName, TFieldState];

        if (overriddenFields.length > 0) {
            return showPushdown({
                header: 'Problem Processing Your Request.',
                description:
                    'The following fields and/or sections require your attention:',
                message: [],
                // TODO: Figure out these type conversions. Or better yet, update sortPushdownErrors typing
                fieldErrors: sortPushdownErrors(overriddenFields as unknown as [string, string][]),
                monthlyIncome: acquisitionData?.monthlyIncome?.value || '',
                color: 'error',
            });
        }
    };

    return {
        handleErrorPushdown,
    };
};

export default useHandleErrorPushdown;